/**
 * WritersDashboardApplication - Main application class for WritersDashboardApp_v2
 * Dashboard for self-published authors to manage their writing workflow
 */

import * as Y from 'yjs';
import { BaseApplication } from '../../../services/baseApplication/BaseApplication';
import { AppConfig } from '../../../services/baseApplication/types';
import { 
  WRITERS_DASHBOARD_CONFIG, 
  DashboardConfig, 
  DashboardMetrics, 
  WritingStep,
  StepAction,
  DEFAULT_WRITING_STEPS,
  STEPS_VERSION
} from './WritersDashboardConfig';
import { createCollaborationProvider } from '../../../services/collaboration';
import { authService } from '../../../services/auth/authService';
import { entityService } from '../../../services/entityService';
import { generateUserColor } from '../../shared/appHelpers';

export class WritersDashboardApplication extends BaseApplication {
  private configMap?: Y.Map<any>;
  private metricsMap?: Y.Map<any>;
  private stepsArray?: Y.Array<Y.Map<any>>;
  private dataChangeHandlers: Set<() => void> = new Set();
  private spaceId: string = '';
  
  constructor() {
    super();
    this.suggestionMode = false;
  }
  
  protected getConfig(): AppConfig {
    return WRITERS_DASHBOARD_CONFIG;
  }

  public getSpaceId(): string {
    return this.spaceId;
  }
  
  async load(entityId: string, options: { offline?: boolean } = {}): Promise<void> {
    console.log('🚀 [WritersDashboardApplication] === LOADING DASHBOARD ===', entityId);
    
    const entity = await entityService.get(entityId);
    if (!entity) throw new Error(`Dashboard not found: ${entityId}`);

    this.spaceId = (entity as any).spaceId || '';
    
    console.log('📦 [WritersDashboardApplication] Entity fetched:', {
      name: entity.name,
      entityType: entity.entityType,
      hasBase64: !!entity.skeleton?.base64
    });
    
    await this.initialize(entityId);
    
    console.log('📥 [WritersDashboardApplication] Creating Y.Doc...');
    this.ydoc = new Y.Doc();
    
    if (entity.skeleton?.base64 && this.ydoc) {
      console.log('📥 [WritersDashboardApplication] Loading base64 state...');
      try {
        this.loadBase64State(entity.skeleton.base64);
        console.log('✅ [WritersDashboardApplication] Base64 state loaded');
      } catch (error) {
        console.error('❌ [WritersDashboardApplication] Failed to load state:', error);
        this.emit('loadError', { error, message: 'Failed to load dashboard data' });
      }
    }
    
    if (this.ydoc) {
      this.configMap = this.ydoc.getMap('config');
      this.metricsMap = this.ydoc.getMap('metrics');
      this.stepsArray = this.ydoc.getArray('writingSteps');
      
      console.log('📋 [WritersDashboardApplication] Maps created');
      
      console.log('[WritersDashboardApplication] 🔧 Setting up auto-save...');
      this.setupAutoSave();
      console.log('[WritersDashboardApplication] ✅ Auto-save enabled');
      
      if (this.stepsArray.length === 0) {
        console.log('🆕 [WritersDashboardApplication] New dashboard, initializing...');
        this.initializeEmptyDashboard();
      } else {
        // Deduplicate steps affected by prior double-init bug
        this.deduplicateSteps();
        // Migrate steps to the current version if needed
        this.migrateStepsIfNeeded();
        // Backfill initialization flag for dashboards saved before it was introduced
        if (!this.configMap.get('hasBeenInitialized')) {
          this.ydoc.transact(() => {
            this.configMap!.set('hasBeenInitialized', true);
          }, 'local');
        }
      }
      
      this.setupDataObservers();
    }
    
    if (!options.offline && this.ydoc) {
      console.log('[WritersDashboardApplication] 🔌 Setting up collaboration...');
      await this.setupCollaborationWithDoc(entityId, this.ydoc);
    }
    
    console.log('✅ [WritersDashboardApplication] === DASHBOARD LOAD COMPLETE ===');
  }
  
  private setupDataObservers(): void {
    if (!this.configMap || !this.metricsMap || !this.stepsArray) return;
    
    const notifyChange = () => {
      this.dataChangeHandlers.forEach(handler => handler());
    };
    
    this.configMap.observe(notifyChange);
    this.metricsMap.observe(notifyChange);
    this.stepsArray.observeDeep(notifyChange);
    
    console.log('✅ [WritersDashboardApplication] Data observers setup');
  }
  
  public onDataChange(handler: () => void): () => void {
    this.dataChangeHandlers.add(handler);
    return () => {
      this.dataChangeHandlers.delete(handler);
    };
  }
  
  private initializeEmptyDashboard(): void {
    if (!this.configMap || !this.metricsMap || !this.stepsArray || !this.ydoc) return;
    
    console.log('🔧 [initializeEmptyDashboard] Creating default dashboard...');
    
    this.ydoc.transact(() => {
      this.configMap!.set('hasBeenInitialized', true);
      this.configMap!.set('stepsVersion', STEPS_VERSION);
      this.configMap!.set('isConfigured', false);
      this.configMap!.set('goalsHidden', false);
      this.configMap!.set('wordCountGoal', 0);
      this.configMap!.set('dailyWritingGoal', 0);
      this.configMap!.set('targetCompletionDate', '');
      
      this.metricsMap!.set('currentWordCount', 0);
      this.metricsMap!.set('todaysWordCount', 0);
      this.metricsMap!.set('lastUpdated', Date.now());
      this.metricsMap!.set('lastDayTracked', new Date().toISOString().split('T')[0]);
      
      if (this.stepsArray!.length === 0) {
        this.pushDefaultSteps();
      }
    }, 'local');
    
    console.log('✅ [initializeEmptyDashboard] Default dashboard created');
  }

  /**
   * Migrate steps to the current STEPS_VERSION.
   *
   * IMPORTANT: We must NOT delete existing Y.Map items and reinsert new ones.
   * Each Y.Map pushed into a Y.Array gets a new unique Y.js clock ID. If the
   * WebSocket collaboration server still holds the old items (different IDs),
   * the CRDT merge treats old-server items and new-client items as concurrent
   * independent inserts and keeps BOTH — producing duplicate steps.
   *
   * Instead we mutate the existing Y.Map entries in place.  Y.Map.set() has
   * Last-Writer-Wins semantics, so the local update always wins over the
   * server's stale value at sync time.  The items keep their original IDs,
   * the server sees a simple field update (not a new insert), and the user's
   * `completed` state is preserved across migrations.
   *
   * For steps that are genuinely new in this version, we insert them once.
   * A single insert merges cleanly because the server has never seen that ID.
   */
  private migrateStepsIfNeeded(): void {
    if (!this.configMap || !this.stepsArray || !this.ydoc) return;

    const savedVersion = this.configMap.get('stepsVersion') || 0;
    if (savedVersion >= STEPS_VERSION) return;

    console.log(`🔄 [migrateStepsIfNeeded] Migrating steps v${savedVersion} → v${STEPS_VERSION}`);

    this.ydoc.transact(() => {
      // Index existing Y.Maps by step id so we can update them in place.
      const existingById = new Map<string, Y.Map<any>>();
      for (let i = 0; i < this.stepsArray!.length; i++) {
        const stepMap = this.stepsArray!.get(i);
        const id: string = stepMap?.get('id') || '';
        if (id) existingById.set(id, stepMap);
      }

      for (const step of DEFAULT_WRITING_STEPS) {
        const existing = existingById.get(step.id);
        if (existing) {
          // Mutate fields in place — no new Y.js IDs, no CRDT duplicate risk.
          existing.set('actions', JSON.stringify(step.actions));
          existing.set('name', step.name);
          existing.set('instructions', step.instructions);
          existing.set('order', step.order);
        } else {
          // Genuinely new step (added in this version). Insert once.
          const stepMap = new Y.Map();
          stepMap.set('id', step.id);
          stepMap.set('name', step.name);
          stepMap.set('instructions', step.instructions);
          stepMap.set('actions', JSON.stringify(step.actions));
          stepMap.set('order', step.order);
          stepMap.set('completed', false);
          this.stepsArray!.push([stepMap]);
        }
      }

      this.configMap!.set('stepsVersion', STEPS_VERSION);
      this.configMap!.set('hasBeenInitialized', true);
    }, 'local');

    console.log('✅ [migrateStepsIfNeeded] Migration complete');
  }

  private pushDefaultSteps(): void {
    for (const step of DEFAULT_WRITING_STEPS) {
      const stepMap = new Y.Map();
      stepMap.set('id', step.id);
      stepMap.set('name', step.name);
      stepMap.set('instructions', step.instructions);
      stepMap.set('actions', JSON.stringify(step.actions));
      stepMap.set('order', step.order);
      stepMap.set('completed', false);
      this.stepsArray!.push([stepMap]);
    }
  }

  private deduplicateSteps(): void {
    if (!this.stepsArray || !this.ydoc) return;

    const seenIds = new Set<string>();
    const indicesToRemove: number[] = [];

    for (let i = 0; i < this.stepsArray.length; i++) {
      const stepMap = this.stepsArray.get(i);
      const id: string = stepMap?.get('id') || '';
      if (seenIds.has(id)) {
        indicesToRemove.push(i);
      } else {
        seenIds.add(id);
      }
    }

    if (indicesToRemove.length === 0) return;

    console.log(`🧹 [deduplicateSteps] Removing ${indicesToRemove.length} duplicate step(s)`);

    this.ydoc.transact(() => {
      for (let i = indicesToRemove.length - 1; i >= 0; i--) {
        this.stepsArray!.delete(indicesToRemove[i], 1);
      }
    }, 'local');

    console.log('✅ [deduplicateSteps] Deduplication complete');
  }

  public getConfiguration(): DashboardConfig {
    if (!this.configMap) {
      return {
        isConfigured: false,
        goalsHidden: false,
        wordCountGoal: 0,
        dailyWritingGoal: 0,
        targetCompletionDate: ''
      };
    }
    
    return {
      isConfigured: this.configMap.get('isConfigured') || false,
      goalsHidden: this.configMap.get('goalsHidden') || false,
      wordCountGoal: this.configMap.get('wordCountGoal') || 0,
      dailyWritingGoal: this.configMap.get('dailyWritingGoal') || 0,
      targetCompletionDate: this.configMap.get('targetCompletionDate') || ''
    };
  }
  
  public updateConfiguration(config: Partial<DashboardConfig>): void {
    if (!this.configMap || !this.ydoc) return;
    
    console.log('📝 [updateConfiguration] Updating config:', config);
    
    this.ydoc.transact(() => {
      if (config.isConfigured !== undefined) {
        this.configMap!.set('isConfigured', config.isConfigured);
      }
      if (config.goalsHidden !== undefined) {
        this.configMap!.set('goalsHidden', config.goalsHidden);
      }
      if (config.wordCountGoal !== undefined) {
        this.configMap!.set('wordCountGoal', config.wordCountGoal);
      }
      if (config.dailyWritingGoal !== undefined) {
        this.configMap!.set('dailyWritingGoal', config.dailyWritingGoal);
      }
      if (config.targetCompletionDate !== undefined) {
        this.configMap!.set('targetCompletionDate', config.targetCompletionDate);
      }
    }, 'local');
    
    console.log('✅ [updateConfiguration] Config updated');
  }
  
  public getMetrics(): DashboardMetrics {
    if (!this.metricsMap) {
      return {
        currentWordCount: 0,
        todaysWordCount: 0,
        lastUpdated: Date.now(),
        lastDayTracked: new Date().toISOString().split('T')[0]
      };
    }
    
    const today = new Date().toISOString().split('T')[0];
    const lastDayTracked = this.metricsMap.get('lastDayTracked');
    
    if (lastDayTracked && lastDayTracked !== today && this.ydoc) {
      this.ydoc.transact(() => {
        this.metricsMap!.set('todaysWordCount', 0);
        this.metricsMap!.set('lastDayTracked', today);
      }, 'local');
    }
    
    return {
      currentWordCount: this.metricsMap.get('currentWordCount') || 0,
      todaysWordCount: this.metricsMap.get('todaysWordCount') || 0,
      lastUpdated: this.metricsMap.get('lastUpdated') || Date.now(),
      lastDayTracked: this.metricsMap.get('lastDayTracked') || today
    };
  }
  
  public updateMetrics(metrics: Partial<DashboardMetrics>): void {
    if (!this.metricsMap || !this.ydoc) return;
    
    this.ydoc.transact(() => {
      if (metrics.currentWordCount !== undefined) {
        this.metricsMap!.set('currentWordCount', metrics.currentWordCount);
      }
      if (metrics.todaysWordCount !== undefined) {
        this.metricsMap!.set('todaysWordCount', metrics.todaysWordCount);
      }
      this.metricsMap!.set('lastUpdated', Date.now());
    }, 'local');
  }
  
  public getWritingSteps(): WritingStep[] {
    if (!this.stepsArray) return [];

    const optionalIds = new Set(
      DEFAULT_WRITING_STEPS.filter((s) => s.optional).map((s) => s.id)
    );

    const steps: WritingStep[] = [];
    for (let i = 0; i < this.stepsArray.length; i++) {
      const stepMap = this.stepsArray.get(i);
      if (!stepMap) continue;

      let actions: StepAction[] = [];
      try {
        const raw = stepMap.get('actions');
        if (raw) actions = JSON.parse(raw);
      } catch {
        actions = [];
      }

      const id = stepMap.get('id') || '';
      steps.push({
        id,
        name: stepMap.get('name') || '',
        instructions: stepMap.get('instructions') || '',
        actions,
        order: stepMap.get('order') || 0,
        completed: stepMap.get('completed') || false,
        optional: optionalIds.has(id)
      });
    }
    
    return steps.sort((a, b) => a.order - b.order);
  }
  
  public toggleStepCompleted(stepId: string): void {
    if (!this.stepsArray || !this.ydoc) return;
    
    console.log('📝 [toggleStepCompleted] Toggling step:', stepId);
    
    this.ydoc.transact(() => {
      for (let i = 0; i < this.stepsArray!.length; i++) {
        const stepMap = this.stepsArray!.get(i);
        if (stepMap.get('id') === stepId) {
          const currentValue = stepMap.get('completed') || false;
          stepMap.set('completed', !currentValue);
          console.log('✅ [toggleStepCompleted] Toggled to:', !currentValue);
          break;
        }
      }
    }, 'local');
  }
  
  public calculateProgress(): {
    wordCountProgress: number;
    stepsProgress: number;
    daysRemaining: number;
    dailyWordsNeeded: number;
  } {
    const config = this.getConfiguration();
    const metrics = this.getMetrics();
    const steps = this.getWritingSteps();
    
    const wordCountProgress = config.wordCountGoal > 0 
      ? (metrics.currentWordCount / config.wordCountGoal) * 100 
      : 0;
    
    const completedSteps = steps.filter(s => s.completed).length;
    const stepsProgress = steps.length > 0 
      ? (completedSteps / steps.length) * 100 
      : 0;
    
    let daysRemaining = 0;
    let dailyWordsNeeded = 0;
    
    if (config.targetCompletionDate) {
      const targetDate = new Date(config.targetCompletionDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      targetDate.setHours(0, 0, 0, 0);
      const timeDiff = targetDate.getTime() - today.getTime();
      daysRemaining = Math.max(0, Math.ceil(timeDiff / (1000 * 60 * 60 * 24)));
      
      if (daysRemaining > 0 && config.wordCountGoal > metrics.currentWordCount) {
        dailyWordsNeeded = Math.ceil(
          (config.wordCountGoal - metrics.currentWordCount) / daysRemaining
        );
      } else if (daysRemaining === 0 && config.wordCountGoal > metrics.currentWordCount) {
        dailyWordsNeeded = config.wordCountGoal - metrics.currentWordCount;
      }
    }
    
    return {
      wordCountProgress: Math.min(100, wordCountProgress),
      stepsProgress: Math.min(100, stepsProgress),
      daysRemaining,
      dailyWordsNeeded
    };
  }
  
  private async setupCollaborationWithDoc(entityId: string, ydoc: Y.Doc): Promise<void> {
    const authUser = authService.getCurrentUser();
    if (!authUser) {
      throw new Error('Not authenticated');
    }
    
    const currentUser = {
      id: authUser.id,
      name: authUser.name || authUser.email || 'User',
      color: generateUserColor(authUser.id)
    };
    
    this.collaborationProvider = createCollaborationProvider({
      documentId: `doc:${entityId}`,
      user: currentUser,
      websocketUrl: this.getWebSocketUrl(),
      authToken: '',
      offline: false,
      doc: ydoc,
    });
    
    this.collaborationProvider.connect();
    
    await new Promise<void>((resolve) => {
      let timeoutId: NodeJS.Timeout | null = null;
      let syncedHandler: (() => void) | null = null;
      let errorHandler: ((error: Error) => void) | null = null;
      
      const cleanup = () => {
        if (timeoutId) clearTimeout(timeoutId);
        if (syncedHandler && this.collaborationProvider) {
          this.collaborationProvider.off('synced', syncedHandler);
        }
        if (errorHandler && this.collaborationProvider) {
          this.collaborationProvider.off('error', errorHandler);
        }
      };
      
      timeoutId = setTimeout(() => {
        console.log('⏱️ [WritersDashboardApplication] WebSocket timeout');
        cleanup();
        resolve();
      }, 5000);
      
      syncedHandler = () => {
        console.log('✅ [WritersDashboardApplication] WebSocket synced');
        cleanup();
        resolve();
      };
      
      errorHandler = (error: Error) => {
        console.error('❌ [WritersDashboardApplication] WebSocket error:', error);
        cleanup();
        resolve();
      };
      
      this.collaborationProvider!.on('synced', syncedHandler);
      this.collaborationProvider!.on('error', errorHandler);
    });
  }
  

}
